import styled, { keyframes } from "styled-components";
import React, { useState, ReactElement, useEffect } from "react";

import { hamletUser } from "../../../common/hamlet/hamlet-user";

import { FormattedMessage, IntlProvider } from "react-intl-next";

import { GUPSProductKeys } from "../../utils/unifed-profile.schema";
import tracking from "../../../utils/tracking";
import storage from "../../../utils/storage";

import CrossIcon from "@atlaskit/icon/glyph/cross";
import { JiraMarkBrandInverse } from "../../logos/jira-mark-brand-inverse";
import { ConfluenceMarkBrandInverse } from "../../logos/confluence-mark-brand-inverse";
import { JiraServiceManagementMarkBrandV2 } from "../../logos/jiraservicemanagementv2-mark-brand";

import Button from "@atlaskit/button";

import ReturnToProductModalCTA from "./ReturnToProductModalCTA";
import Environment from "../../../common/environment";
import imkt from "../../../imkt";

export interface ReturnToProductModalV2Data {
  productKey: GUPSProductKeys;
  productName: string;
  siteName: string;
  productMark: ReactElement;
  instanceHref: string;
  ctaButton?: ReactElement;
}

interface IconContainerProps {
  bgColor: string; // bgColor changes based on product logo
}

const IconContainer = styled.span<IconContainerProps>`
  display: flex;
  height: 32px;
  width: 32px;
  background-color: ${({ bgColor }) => bgColor};
  border-radius: 8px;
  align-items: center;
  justify-content: center;

  svg {
    margin-bottom: 0px !important;
  }
`;

const productIconMap = {
  // logo width and height taken from app-switcher
  jira: (
    <IconContainer bgColor="#1868db" className="jira-product-mark">
      <JiraMarkBrandInverse width="20px" height="20px" />
    </IconContainer>
  ),
  confluence: (
    <IconContainer bgColor="#1868db" className="confluence-product-mark">
      <ConfluenceMarkBrandInverse width="16px" height="15px" />
    </IconContainer>
  ),
  jiraservicemanagement: (
    <IconContainer bgColor="#ffc716" className="jsm-product-mark">
      <JiraServiceManagementMarkBrandV2 width="18px" height="21px" />
    </IconContainer>
  ),
};

const getSiteURL = (productKey: string, siteUrl: string) => {
  const identityUrl = Environment.getIdentityBaseUrl();
  const redirectUrl = identityUrl + "/login?prompt=none&continue=";

  if (productKey === "jira-software.ondemand") {
    return `${redirectUrl}${siteUrl}/jira/projects?types=business%2Csoftware`;
  } else if (productKey === "confluence.ondemand") {
    return `${redirectUrl}${siteUrl}/wiki`;
  } else if (productKey === "jira-servicedesk.ondemand") {
    return `${redirectUrl}${siteUrl}/jira/projects?types=service_desk`;
  }
  return "";
};

const getCTAButton = (
  productKey: GUPSProductKeys,
  siteUrl: string
): React.JSX.Element => {
  const url = getSiteURL(productKey, siteUrl);

  return (
    <Button
      href={url}
      target="_blank"
      appearance="primary"
      sizes="small"
      className="modal-cta-button"
      onClick={() => {
        localStorage.removeItem(localStorageItem);
        tracking.trackEvent("returnToProductModalV2-cta-clicked", {
          event: "clicked",
          eventComponent: "button",
          eventContainer: "returnToProductModalV2",
          labelEnglish: "Go",
          href: url,
          url: location.href,
          platform: "magnolia",
        });
      }}
    >
      <FormattedMessage
        id="returnToProductModalCtaText"
        defaultMessage={"Go"}
      />
    </Button>
  );
};

const scaleUpKeyframes = keyframes`
  0% { opacity: 0; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 1; transform: scale(1.0); }
`;

const scaleDownKeyframes = keyframes`
  from { opacity: 1; transform: scale(1)}
  to { opacity: 0; transform: scale(0.95)}
`;

const ModalPopup = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: var(
    --ds-shadow-overlay,
    0 8px 12px 8px rgba(9, 30, 66, 0.25),
    0 0 1px 1px rgba(9, 30, 66, 0.13)
  );
  height: auto !important;
  inset-block-start: 50px;
  width: 415px;
  padding: 24px;
  position: fixed;
  z-index: 2;
  margin-block-start: 40px;
  inset-inline-end: 20px;
  display: block;

  @media (max-width: 768px) {
    display: none !important;
  }

  &.open {
    animation: ${scaleUpKeyframes} 0.4s;
  }

  &.close {
    transition: all 0.4s;
    opacity: 0;
    animation: ${scaleDownKeyframes} 0.4s;
    pointer-events: none;
  }
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: start;
`;

const ButtonContainer = styled.div`
  position: absolute;
  inset-inline-end: 26px;
  inset-block-start: 24px;

  button {
    align-items: center;
    height: 24px;

    background: none;
    padding: 0px;

    border: none;
    display: flex;
    float: inline-end;
    flex-direction: column;
    position: relative;
    width: 24px;

    &:hover {
      cursor: pointer;
    }

    &:focus {
      border: 1px #18191a solid;
    }
  }
`;

const TextContainer = styled.div`
  max-width: 463px;
  overflow-wrap: break-word;
  text-align: left;
  display: block;

  h5 {
    color: black;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    margin: 0;
    text-transform: none;
  }

  p {
    color: black !important;
    font-size: 16px !important;
    margin: 0 !important;
  }
`;

const localStorageItem = "return_to_product_modal_dismissal";

type OnGraphqlApiRequest = (query: string) => Promise<Response>;

const onGraphqlApiRequest: OnGraphqlApiRequest = async (query) => {
  const graphqlUrl = `${Environment.getAPIPrivateURL()}/graphql`;
  return fetch(graphqlUrl, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-type": "application/json",
    },
    body: JSON.stringify({ query: query }),
  });
};

const getGUPsData = async (userAaid: string) => {
  try {
    const query = ` 
      query getProfile {
        growthUnifiedProfile_getUnifiedProfile(
          accountId: "${userAaid}"
        ) {
          sites {
            displayName
            url
            products {
              productName
              productKey
              provisionedAt
              d1to6Eligible
              d1to6Active
            }
          } 
        }
      }
    `;
    const response = await onGraphqlApiRequest(query);

    if (response.ok) {
      const responseJson = await response.json();
      return responseJson.data.growthUnifiedProfile_getUnifiedProfile.sites;
    }
  } catch (error) {
    console.error("Error fetching GUPS unified profile:", error);
    return null;
  }
};

type GUPSProduct = {
  productName: string;
  productKey: string;
  d1to6Eligible: boolean;
  d1to6Active: boolean;
  provisionedAt: string;
};

type GUPSSite = {
  displayName: string;
  url: string;
  products: GUPSProduct[];
};

function mapProductKeyToName(productKey: string): string {
  const productKeyMap: { [key: string]: string } = {
    "jira-software.ondemand": "Jira",
    "confluence.ondemand": "Confluence",
    "jira-servicedesk.ondemand": "Jira Service Management",
  };

  if (productKey in productKeyMap) {
    return productKeyMap[productKey];
  } else {
    throw new Error(`Unknown Product Key: ${productKey}`);
  }
}

const getEligibleSites = (sites: GUPSSite[]): ReturnToProductModalV2Data[] => {
  const allProducts: (GUPSProduct & {
    siteUrl?: string;
    siteName?: string;
  })[] = (sites ?? []).flatMap((site) =>
    (site.products ?? []).map((product) => ({
      ...product,
      siteUrl: site.url,
      siteName: site.displayName,
    }))
  );
  const finalProducts = [] as ReturnToProductModalV2Data[];

  const calculateActivityClass = (product: GUPSProduct): number => {
    if (product.d1to6Eligible) {
      return product.d1to6Active ? 2 : 1;
    }
    return 3; // Rank products that are not d1to6Eligible the lowest
  };

  const sortedProducts = allProducts
    .sort((productA, productB) => {
      const activityClassA = calculateActivityClass(productA);
      const activityClassB = calculateActivityClass(productB);

      if (activityClassA !== activityClassB) {
        return activityClassA < activityClassB ? -1 : 1;
      }

      // If activity classes are the same, sort by productKey
      // Custom order for productKey
      const productOrder: Record<GUPSProductKeys, number> = {
        "jira-software.ondemand": 1,
        "jira-servicedesk.ondemand": 2,
        "confluence.ondemand": 3,
      };
      const productKeyAOrder =
        productOrder[productA.productKey as GUPSProductKeys] ||
        Number.MAX_SAFE_INTEGER;
      const productKeyBOrder =
        productOrder[productB.productKey as GUPSProductKeys] ||
        Number.MAX_SAFE_INTEGER;
      if (productKeyAOrder !== productKeyBOrder) {
        return productKeyAOrder - productKeyBOrder;
      }

      // If same product, sort by provisioning date (more recent dates first)
      const provisioningDateA = new Date(productA.provisionedAt).getTime();
      const provisioningDateB = new Date(productB.provisionedAt).getTime();
      if (provisioningDateA !== provisioningDateB) {
        return provisioningDateB - provisioningDateA; // More recent dates // first
      }

      // If all else is the same, maintain original order
      return 0;
    });

  let jiraSoftwareSite, confluenceSite, jiraServiceManagementSite; // take the highest ranked site for each product

  for (const product of sortedProducts) {
    if (!jiraSoftwareSite && product.productKey === "jira-software.ondemand") {
      jiraSoftwareSite = {
        productKey: product.productKey as GUPSProductKeys,
        productName: mapProductKeyToName(product.productKey),
        siteName: product.siteName ?? "",
        productMark: productIconMap.jira,
        instanceHref: product.siteUrl ?? "",
        ctaButton: getCTAButton(
          product.productKey as GUPSProductKeys,
          product.siteUrl ?? ""
        ),
      };
      finalProducts.push(jiraSoftwareSite);
    }
    if (!confluenceSite && product.productKey === "confluence.ondemand") {
      confluenceSite = {
        productKey: product.productKey as GUPSProductKeys,
        productName: mapProductKeyToName(product.productKey),
        siteName: product.siteName ?? "",
        productMark: productIconMap.confluence,
        instanceHref: product.siteUrl ?? "",
        ctaButton: getCTAButton(
          product.productKey as GUPSProductKeys,
          product.siteUrl ?? ""
        ),
      };
      finalProducts.push(confluenceSite);
    }
    if (
      !jiraServiceManagementSite &&
      product.productKey === "jira-servicedesk.ondemand"
    ) {
      jiraServiceManagementSite = {
        productKey: product.productKey as GUPSProductKeys,
        productName: mapProductKeyToName(product.productKey),
        siteName: product.siteName ?? "",
        productMark: productIconMap.jiraservicemanagement,
        instanceHref: product.siteUrl ?? "",
        ctaButton: getCTAButton(
          product.productKey as GUPSProductKeys,
          product.siteUrl ?? ""
        ),
      };
      finalProducts.push(jiraServiceManagementSite);
    }

    // Break early if we've found all three
    if (jiraSoftwareSite && confluenceSite && jiraServiceManagementSite) {
      break;
    }
  }

  return finalProducts as ReturnToProductModalV2Data[];
};

function filterGUPSResponse(sites: GUPSSite[]): GUPSSite[] {
  return sites
    .map((site) => ({
      ...site,
      products: site.products, // Display all products regardless of d1to6Eligible status (v3 experiment scope)
    }))
    .filter((site) => site.products.length > 0) // Remove sites with no products
    .reverse(); // Reverse order of list to prioritize most recent provision date
}

type ReturnToProductModalV2Type = "default" | "jsm";
const returnToProductModalTypeMap: Record<
  ReturnToProductModalV2Type,
  string[]
> = {
  default: [
    "jira-software.ondemand",
    "jira-servicedesk.ondemand",
    "confluence.ondemand",
  ],
  jsm: ["jira-servicedesk.ondemand"],
};

const getUserProducts = async (
  userAaid: string
): Promise<ReturnToProductModalV2Data[]> => {
  const userSites = await getGUPsData(userAaid);
  if (!userSites) {
    return [];
  }

  const usersProducts = filterGUPSResponse(userSites);
  const finalProductList = getEligibleSites(usersProducts);

  return finalProductList;
};

interface ReturnToProductModalProps {
  messages?: Record<string, string>;
  modalTypeOverride?: ReturnToProductModalV2Type;
}

const ReturnToProductModal = (props: ReturnToProductModalProps) => {
  const { messages, modalTypeOverride } = props;
  const currentDate = new Date().toISOString();
  const previousModalDismissalDate = localStorage.getItem(localStorageItem);

  // Cases with validClickLogic:
  //    a. no previousModalDismissalDate (no previous click actions)
  //    b. previousModalDismissalDate is not the same day as currentDay
  const validClickLogic = (() => {
    if (previousModalDismissalDate) {
      if (previousModalDismissalDate === "always_disable") {
        return false;
      } else {
        const previousClickDate = new Date(previousModalDismissalDate);
        previousClickDate.setHours(24, 0, 0); // modifies previousClickData into midnight point for the next day in that timezone

        return previousClickDate <= new Date(); // return valid click logic if current click attempt is after midnight on the day of the previous click
      }
    }
    return true; // case of no previous clicks
  })();

  const modalClickFunctionality = () => {
    setIsOpen(false);

    // update localStorage item accordingly
    if (previousModalDismissalDate) {
      if (validClickLogic) {
        // condition is only true if there was a single previous click event uninterrupted by CTA activity
        localStorage.setItem(localStorageItem, "always_disable"); // set to always disable after 2nd click in a row
      }
    } else {
      // no previous click event has occured, record first one
      localStorage.setItem(localStorageItem, currentDate);
    }
    tracking.trackEvent("returnToProductModalV2-closed", {
      event: "clicked",
      eventComponent: "button",
      eventContainer: "returnToProductModalV2",
      labelEnglish: "close",
      url: location.href,
      platform: "magnolia",
    });
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [aaid, setAaid] = useState(undefined);
  const [userName, setUserName] = useState(undefined);
  const [products, setProducts] = useState([] as ReturnToProductModalV2Data[]);

  useEffect(() => {
    if (storage.getCookie("login_user_detected") === "true") {
      hamletUser
        .getStargateProfile(true)
        .then(() => {
          setIsLoggedIn(hamletUser.isLoggedIn());
          setAaid(hamletUser.getUserAaid());

          const fullName = hamletUser.getDisplayName();
          setUserName(fullName.trim().split(" ")[0] || "");
        })
        .catch((e) => console.error("Error getting startgate user profile", e));
    }
  }, []);

  useEffect(() => {
    const handleModalLogic = async () => {
      if (!isLoggedIn || !aaid) {
        setIsOpen(false);
        return;
      }

      try {
        const usersProducts = await getUserProducts(aaid);

        if (usersProducts.length === 0) {
          setIsOpen(false);
          return;
        }

        const isBacktoProductV3 =
          await imkt.utils.featureGate.getExperimentValue(
            "gwp-8217-back-to-product-v3",
            "cohort",
            false
          );

        if (isBacktoProductV3 && validClickLogic) {
          const modalType = modalTypeOverride ?? "default"; // v3 experiment displaying default behavior for all 3 products (Jira, Confluence, JSM)
          const eligibleProducts = returnToProductModalTypeMap[modalType];
          let products = usersProducts.filter((product) =>
            eligibleProducts.includes(product.productKey)
          );
          if (products.length === 0) {
            setIsOpen(false);
            return;
          }

          // Back to Product v3 Experiment: only showing one product in the modal
          products = [products[0]];

          setProducts(products);
          setIsOpen(true);

          const properties = {
            event: "viewed",
            eventComponent: "returnToProductModalV2",
            products: products.map((product) => product.productName),
            url: location.href,
            platform: "magnolia",
          };
          tracking.trackUIEvent("return-to-product-modal-v2", properties);
        } else {
          setIsOpen(false);
        }
      } catch (error) {
        console.error(
          "Error fetching experiment value or user products:",
          error
        );
      }
    };
    handleModalLogic();
  }, [isLoggedIn, aaid, validClickLogic]);

  useEffect(() => {
    //@ts-ignore
    const handleClick = (e) => {
      // added classname identifiers to app-switcher - modal should be open when app-switcher is not open
      if (document.querySelector(".app-switcher-menu-open")) {
        setIsOpen(false);
      } else if (document.querySelector(".app-switcher-menu-close")) {
        setIsOpen(true);
      }
    };

    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  if (!isOpen || products.length === 0) {
    return null;
  }

  return (
    <IntlProvider
      locale={document.documentElement.lang}
      messages={props.messages || {}}
    >
      <ModalPopup
        className={`${isOpen ? "open" : "close"} return-to-product-modal`}
      >
        <ModalContainer>
          <ButtonContainer>
            <button
              className="return-to-product-modal-button"
              onClick={modalClickFunctionality}
            >
              <CrossIcon label={"close"} size={"medium"} />
            </button>
          </ButtonContainer>
          <TextContainer>
            <h5>
              <FormattedMessage
                id="returnToProductModalHeroText"
                defaultMessage={"Welcome back, {name}"}
                values={{ name: userName }}
              />
            </h5>
            <p>
              <FormattedMessage
                id="returnToProductModalSubText"
                defaultMessage={"Pick up where you left off."}
              />
            </p>
          </TextContainer>
          <ReturnToProductModalCTA products={products} />
        </ModalContainer>
      </ModalPopup>
    </IntlProvider>
  );
};

export default ReturnToProductModal;
